import { datadogRum } from '@datadog/browser-rum'
import { onlineManager } from '@tanstack/react-query'
import { getSession, useSession } from 'next-auth/react'
import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { DEFAULT_TIMINGS } from './timings'

export type SwitchOrgOptions = { redirectTo?: string }

type TokenContextValue = {
  /** Get a valid auth token. Returns cached token if valid, or fetches fresh one. */
  getToken: () => Promise<string>
  /** Switch to a different organisation. Updates localStorage and hard refreshes. */
  switchOrg: (organisationId: string, options?: SwitchOrgOptions) => Promise<void>
  /** Whether an org switch is in progress */
  isSwitching: boolean
}

export const TokenContext = createContext<TokenContextValue>({
  getToken: async () => {
    throw new Error('TokenProvider not initialized')
  },
  switchOrg: async () => {
    throw new Error('TokenProvider not initialized')
  },
  isSwitching: false,
})

type LatestTokenRef = {
  token: string | Promise<string>
  expiryTimestamp: number
}

// localStorage key for target org - used for cross-tab sync
export const TARGET_ORG_STORAGE_KEY = 'omnea_target_org_id'

// Helper to safely access localStorage (handles private browsing)
export const getTargetOrg = (): string | null => {
  try {
    return localStorage.getItem(TARGET_ORG_STORAGE_KEY)
  } catch {
    return null
  }
}

export const setTargetOrg = (orgId: string): void => {
  try {
    localStorage.setItem(TARGET_ORG_STORAGE_KEY, orgId)
  } catch {
    // localStorage not available (private browsing)
  }
}

export const sanitizeRedirectTo = (redirectTo?: string): string => {
  if (typeof redirectTo !== 'string' || redirectTo?.length === 0) {
    return '/'
  }

  if (redirectTo.startsWith('/') && !redirectTo.startsWith('//')) {
    return redirectTo
  }

  if (typeof window === 'undefined') {
    return '/'
  }

  try {
    const parsed = new URL(redirectTo)
    if (parsed.origin !== window.location.origin) {
      return '/'
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/'
  } catch {
    return '/'
  }
}

/**
 * TokenProvider - Unified provider for auth token management and org switching.
 *
 * This provider manages auth tokens and org switching with a simple, robust approach:
 *
 * Features:
 * - `getToken()`: Returns current token if valid, or fetches fresh one if expired
 * - `switchOrg()`: Switches organisation via localStorage + hard refresh
 * - Visibility-aware polling: Refreshes tokens proactively when tab is visible
 * - Cross-tab sync: Uses localStorage + storage event to sync org across tabs
 *
 * Org Switch Strategy:
 * 1. Store target org in localStorage (triggers storage event in other tabs)
 * 2. Update session to new org
 * 3. Hard refresh to clear all caches and component state
 *
 * This eliminates race conditions by:
 * - Using hard refresh to clear all in-memory state
 * - Using localStorage for cross-tab communication
 * - Checking org sync on visibility change
 */
export const TokenProvider = ({ children }: { children: ReactNode }) => {
  const session = useSession()
  const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const isRefreshingRef = useRef(false)

  // Org switching state
  const [isSwitching, setIsSwitching] = useState(false)

  // Token caching
  const latestTokenRef = useRef<LatestTokenRef>({
    token: session.data?.token ?? '',
    expiryTimestamp: session.data?.expires != null ? new Date(session.data.expires).getTime() : 0,
  })

  // Update token ref when session changes
  // Always update if token is different (handles org switches where new expiry might be <= old expiry)
  // Also update if same token but newer expiry (handles token refresh)
  if (session.data?.token != null && session.data.expires) {
    const newToken = session.data.token
    const newExpiry = new Date(session.data.expires).getTime()
    const currentToken = latestTokenRef.current.token

    const tokenChanged = currentToken instanceof Promise || currentToken !== newToken
    const expiryExtended = newExpiry > latestTokenRef.current.expiryTimestamp

    if (tokenChanged || expiryExtended) {
      latestTokenRef.current = {
        token: newToken,
        expiryTimestamp: newExpiry,
      }
    }
  }

  // Listen for org changes from OTHER tabs (storage event only fires in other tabs)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === TARGET_ORG_STORAGE_KEY && event.newValue) {
        // Only refresh if we're fully authenticated with a DIFFERENT org
        const currentOrg = session.data?.user?.orgMetadata?.organisationId
        if (session.data?.user?.type === 'fullyAuthenticated' && currentOrg && currentOrg !== event.newValue) {
          window.location.href = '/'
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [session.data?.user?.type, session.data?.user?.orgMetadata?.organisationId])

  const switchOrg = useCallback(
    async (organisationId: string, options?: SwitchOrgOptions): Promise<void> => {
      setIsSwitching(true)
      // Pause all mutations to prevent stale data writes during org switch
      onlineManager.setOnline(false)

      try {
        // 1. Set target org in localStorage (triggers storage event in other tabs)
        setTargetOrg(organisationId)

        // 2. Update session to new org
        await session.update({ organisationId })

        // 3. Hard refresh - clears all caches, component state, pending requests
        const redirectTo = sanitizeRedirectTo(options?.redirectTo)
        window.location.href = redirectTo
      } catch (error) {
        // Restore online mode on error, as page refresh will reset it on success
        onlineManager.setOnline(true)
        // Only reset isSwitching on error - successful switch refreshes the page
        setIsSwitching(false)
        datadogRum.addError(error, { targetOrg: organisationId })
        throw error
      }
    },
    [session]
  )

  // VISIBILITY-AWARE SESSION REFRESH
  useEffect(() => {
    // Only set up for fully authenticated users
    if (session.data?.user?.type !== 'fullyAuthenticated') {
      return
    }

    const activeInterval = DEFAULT_TIMINGS.SESSION_REFRESH_INTERVAL * 1000

    const refreshSession = async () => {
      // Don't refresh during org switch - could cause race conditions
      if (isRefreshingRef.current || isSwitching) {
        return
      }

      isRefreshingRef.current = true
      try {
        await getSession()
      } catch (error) {
        console.error('[TokenProvider] Session refresh failed', error)
      } finally {
        isRefreshingRef.current = false
      }
    }

    const startPolling = (intervalMs: number) => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }

      if (intervalMs <= 0) {
        return
      }

      intervalRef.current = setInterval(() => {
        // Don't poll during org switch
        if (isRefreshingRef.current || document.hidden || isSwitching) {
          return
        }

        const { expiryTimestamp } = latestTokenRef.current
        const timeUntilExpiry = (expiryTimestamp - Date.now()) / 1000

        const wasRecentlyRefreshed = timeUntilExpiry > DEFAULT_TIMINGS.ACCESS_TOKEN_EXPIRY * 0.8

        if (timeUntilExpiry <= DEFAULT_TIMINGS.SESSION_REFRESH_INTERVAL && !wasRecentlyRefreshed) {
          void refreshSession()
        }
      }, intervalMs)
    }

    const handleVisibilityChange = () => {
      // Don't do anything during org switch
      if (isSwitching) return

      if (document.hidden) {
        // Stop polling when hidden
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = undefined
        }
      } else {
        // Tab became visible - first check org sync with localStorage
        const targetOrg = getTargetOrg()
        const currentOrg = session.data?.user?.orgMetadata?.organisationId

        if (targetOrg && currentOrg && targetOrg !== currentOrg) {
          // Out of sync with localStorage - hard refresh to get correct org
          window.location.href = '/'
          return
        }

        // Then normal token refresh
        void refreshSession()
        startPolling(activeInterval)
      }
    }

    // Initialize: start polling if tab is currently visible
    if (!document.hidden && !isSwitching) {
      startPolling(activeInterval)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      isRefreshingRef.current = false
    }
  }, [session.data?.user?.type, session.data?.user?.orgMetadata?.organisationId, isSwitching])

  // GET TOKEN
  const getToken = useCallback(async (): Promise<string> => {
    const { token, expiryTimestamp } = latestTokenRef.current

    // If token is valid, return it
    if (Date.now() < expiryTimestamp && typeof token === 'string' && token !== '') {
      return token
    }

    // If already fetching, return the existing promise
    if (token instanceof Promise) {
      return token
    }

    // Fetch new token
    const tokenPromise: Promise<string> = getSession()
      .then((newSession) => newSession || getSession()) // Retry once
      .then((newSession) => {
        if (newSession?.token == null) {
          throw new Error('Session has no token')
        }

        latestTokenRef.current = {
          token: newSession.token,
          expiryTimestamp: new Date(newSession.expires).getTime(),
        }

        return newSession.token
      })

    // Store promise to deduplicate concurrent calls
    latestTokenRef.current.token = tokenPromise

    return tokenPromise
  }, [])

  return <TokenContext.Provider value={{ getToken, switchOrg, isSwitching }}>{children}</TokenContext.Provider>
}

/**
 * Hook to access TokenProvider functionality.
 *
 * For most components, you only need `getToken`:
 * ```
 * const { getToken } = useTokenProvider()
 * const token = await getToken()
 * ```
 *
 * For org switching:
 * ```
 * const { switchOrg, isSwitching } = useTokenProvider()
 * await switchOrg('new-org-id')
 * ```
 */
export const useTokenProvider = () => useContext(TokenContext)

/**
 * Hook for components that only need getToken.
 */
export const useToken = () => {
  const { getToken } = useTokenProvider()
  return { getToken }
}
